import React, { FC, useState, useEffect } from 'react';
import styles from './PlayerPortalsBurgerMenu.module.scss';
import { usePlayerPortalsBurgerMenu } from './usePlayerPortalsBurgerMenu';
import { sendGAEvent } from '@next/third-parties/google';
import { getPageTrackingDetails } from '@r-and-a-shared-ui/utils';
import { extractRolesNameFromItem } from '@r-and-a-shared-ui/auth-client';
import PlayerPortalsIDCard, {
  PlayerPortalsIDCardProps,
} from '../PlayerPortalsIDCard/PlayerPortalsIDCard';
import { PlayerPortalRestrictedContent } from '../PlayerPortalRestrictedContent/PlayerPortalRestrictedContent';
import {
  BurgerMenuItem,
  PlayerPortalsBurgerMenuItem,
} from './PlayerPortalsBurgerMenuItem';

export interface PlayerPortalsBurgerMenuProps {
  menuItems: BurgerMenuItem[];
  isOpenDefault?: boolean;
  idCard?: PlayerPortalsIDCardProps;
}

export const PlayerPortalsBurgerMenu: FC<PlayerPortalsBurgerMenuProps> = ({
  menuItems,
  isOpenDefault = false,
  idCard,
}) => {
  const { isOpen, onToggle, onClose } =
    usePlayerPortalsBurgerMenu(isOpenDefault);
  const [resetKey, setResetKey] = useState(0);

  const idCardComponentRoles = idCard
    ? extractRolesNameFromItem(idCard.roles)
    : [];

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('menu__open');
      const { pageUrl, pageTitle } = getPageTrackingDetails();
      sendGAEvent({ event: 'open_burger_menu', value: { pageUrl, pageTitle } });
    } else {
      document.body.classList.remove('menu__open');
      setResetKey(k => k + 1);
    }
  }, [isOpen]);

  const handleMenuItemClick = (item: {
    href?: string;
    internal?: boolean;
    title: string;
  }) => {
    onClose();

    const { pageUrl, pageTitle } = getPageTrackingDetails();
    sendGAEvent({
      event: 'burger_menu_click',
      value: {
        pageUrl,
        pageTitle,
        clickUrl: item?.href,
        clickText: item.title,
      },
    });
  };

  return (
    <div className={styles['nav-container']}>
      <nav data-testid="MainMenu" aria-label="Main Navigation">
        <input
          id="toggle-menu"
          className={styles['hidden-toggle-button']}
          type="checkbox"
          checked={isOpen}
          onChange={onToggle}
          aria-controls="burger-menu"
        />
        <label
          className={styles['new-toggler-container']}
          htmlFor="toggle-menu"
        >
          <div className={styles['new-toggler-container__button']}></div>
        </label>
        <div className={styles['menu']}>
          <div className={styles.container}>
            <ul
              className={styles['menu__list']}
              role="menu"
              data-level="0"
              id="burger-menu"
            >
              {menuItems.map((item, index) => (
                <PlayerPortalsBurgerMenuItem
                  key={index}
                  item={item}
                  onClick={() => handleMenuItemClick(item)}
                  level={0}
                  resetKey={resetKey}
                />
              ))}
            </ul>
            {idCard && (
              <PlayerPortalRestrictedContent
                accessRestrictions={{
                  componentRoles: idCardComponentRoles.length
                    ? { required: idCardComponentRoles }
                    : undefined,
                }}
              >
                <PlayerPortalsIDCard {...idCard} />
              </PlayerPortalRestrictedContent>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};
